import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AlertCircle, Lock, ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  allowedDashboards?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  allowedDashboards = [],
}) => {
  const { user, isLoggedIn, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Se verifică autentificarea...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl p-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-red-500/30">
              <Lock className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Acces Interzis</h1>
            <p className="text-gray-300 mb-6">
              Nu aveți permisiunea de a accesa această pagină.
              Rolul dvs. ({user.role}) nu a fost autorizat.
            </p>
            <div className="flex items-center justify-center space-x-2 text-red-400 bg-red-500/10 border-red-500/20 backdrop-blur-sm border rounded-xl p-3 mb-6">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">Accesul necesită: {requiredRoles.join(', ')}</span>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => window.history.back()}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-3 px-6 rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Înapoi</span>
              </button>
              <a
                href={user.dashboard || '/'}
                className="block w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 px-6 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200"
              >
                Mergi la Dashboard-ul Tău
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (allowedDashboards.length > 0 && !allowedDashboards.includes(user.dashboard)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl p-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-yellow-500/30">
              <Lock className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Acces Restricționat</h1>
            <p className="text-gray-300 mb-6">
              Această pagină nu este disponibilă pentru dashboard-ul dvs. ({user.dashboard}).
            </p>
            <a
              href={user.dashboard || '/'}
              className="block w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 px-6 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200"
            >
              Mergi la Dashboard-ul Tău
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
